import { AccountPortalProvider } from '@tonnie/account';
import { LocaleProvider } from '@tonnie/locale';
import type { IAnalyticsClient, IErrorClient } from '@tonnie/observability';
import { ObservabilityProvider } from '@tonnie/observability';
import { LocaleBridge } from '@tonnie/sdk';
import { AppToaster } from '@/core/components/AppToaster';
import { TooltipProvider } from '@tonnie/ui';
import ErrorBoundary from '@/components/shell/ErrorBoundary';
import { ViewportGuard } from '@/components/shell/ViewportGuard';
import { BusinessThemeProvider } from '@/core/app/BusinessThemeProvider';
import { AuthProvider } from '@/core/contexts/AuthContext';
import { BusinessContextProvider } from '@/core/contexts/BusinessContext';
import { CoPilotSidebarProvider } from '@/core/contexts/CoPilotSidebarContext';
import { CoPilotEngineProvider } from '@/features/platform/copilot/context/CoPilotEngineProvider';
import { ObservabilityAuthSync } from '@/core/components/ObservabilityAuthSync';
import { TraceIdBridge } from '@/core/components/TraceIdBridge';
import { BUSINESS_ACCOUNT_PORTAL } from '@/features/platform/auth/account-portal';
import Router from '@/core/routes';

interface AppProps {
  analyticsClient: IAnalyticsClient;
  errorClient: IErrorClient;
}

export default function App({ analyticsClient, errorClient }: AppProps) {
  return (
    <ViewportGuard>
      <ErrorBoundary>
        <LocaleProvider detectLanguage={true} debug={import.meta.env.DEV}>
          <ObservabilityProvider analytics={analyticsClient} errors={errorClient}>
            <TraceIdBridge />
            <LocaleBridge />
            <BusinessThemeProvider>
              <AuthProvider>
                {/* Identity → observability. Inside AuthProvider (and the
                    SDK providers from main.tsx) so it can read the internal
                    user id and the user's tenant memberships. */}
                <ObservabilityAuthSync />
                <BusinessContextProvider>
                  <CoPilotSidebarProvider>
                    <CoPilotEngineProvider>
                      <TooltipProvider>
                        <AppToaster />
                        <AccountPortalProvider value={BUSINESS_ACCOUNT_PORTAL}>
                          <Router />
                        </AccountPortalProvider>
                      </TooltipProvider>
                    </CoPilotEngineProvider>
                  </CoPilotSidebarProvider>
                </BusinessContextProvider>
              </AuthProvider>
            </BusinessThemeProvider>
          </ObservabilityProvider>
        </LocaleProvider>
      </ErrorBoundary>
    </ViewportGuard>
  );
}
